import React, { useEffect } from "react";
import { useBotDemo } from "../../../context/botDemoContext";
import { initSettingDCA } from "../../drawersDemo/DrawerActivateBotDemo";
import DropdownExchange from "../../commons/DropdownExchange";
import DropdownPair from "../../commons/DropdownPair";
import AvailableForTrade from "../../create-bot-setting/common/AvailableForTrade";
import InputWithIcon from "../../commons/InputWithIcon";
import CustomSlider from "../../commons/CustomSlider";
import {
  getSafeNumericValue,
  isInProgressDecimal,
  isValidDecimalInput,
} from "../../../utils";
import LabelWithTooltip from "../../commons/LabelWithTooltip";

// --- Custom Hook for DCA Form Logic ---
const useDCAForm = (initialFormState, userBalance) => {
  const [formDCA, setFormDCA] = React.useState(initialFormState);
  const { settings = {} } = formDCA || {};

  // UPDATED: This handler now manages all real-time validation.
  const handleSettingChange = (name, value) => {
    if (!isValidDecimalInput(value)) return;

    // Define max values for real-time validation
    const maxValues = {
      capital: userBalance,
      // For `amount` input, prevent typing a value > capital
      amount: settings.capital || Infinity,
    };
    const max = maxValues[name] || Infinity;

    const finalValue = isInProgressDecimal(value)
      ? value
      : getSafeNumericValue(value, max);

    setFormDCA((prev) => {
      const newSettings = { ...prev.settings, [name]: finalValue };

      return { ...prev, settings: newSettings };
    });
  };

  const handleBlur = (name, value) => {
    setFormDCA((prev) => {
      const cleanValue = getSafeNumericValue(value);
      const newSettings = { ...prev.settings, [name]: cleanValue };

      if (name === "capital") {
        if (newSettings.amount > newSettings.capital) {
          newSettings.amount = newSettings.capital;
        }
      }

      return { ...prev, settings: newSettings };
    });
  };

  // Slider behavior provides real-time feedback as well.
  const handleSliderChange = (percentage) => {
    const newCapital = parseFloat(
      ((percentage / 100) * (userBalance || 0)).toFixed(2),
    );

    setFormDCA((prev) => {
      const currentAmount = prev.settings.amount;
      const newAmount = currentAmount > newCapital ? newCapital : currentAmount;

      return {
        ...prev,
        settings: {
          ...prev.settings,
          capital: newCapital,
          amount: newAmount,
        },
      };
    });
  };

  const capitalPercentage =
    userBalance > 0
      ? (getSafeNumericValue(settings.capital) / userBalance) * 100
      : 0;

  return {
    formDCA,
    setFormDCA,
    settings,
    handleSettingChange,
    handleBlur,
    handleSliderChange,
    capitalPercentage,
  };
};

// --- Main DCA Component ---
const DCAComponent = ({
  pairs,
  exchanges,
  selectedExchange,
  setSelectedExchange,
  selectedPair,
  setSelectedPair,
  loadingPair,
  loadingExchanges,
  formDCA: initialFormState,
  setFormDCA,
  userBalance,
  errors,
  setErrors,
}) => {
  const {
    formDCA,
    settings,
    handleSettingChange,
    handleBlur,
    handleSliderChange,
    capitalPercentage,
  } = useDCAForm(initialFormState, userBalance);
  const { openDialogBot } = useBotDemo();

  const handleDropdownChange = (setter) => (val) => {
    setter(val);
    if (setter === setSelectedExchange) {
      setSelectedPair(null);
    }
  };

  useEffect(() => {
    setFormDCA(formDCA);
  }, [formDCA, setFormDCA]);

  useEffect(() => {
    if (!openDialogBot || openDialogBot === "step-bot-1") {
      setFormDCA(initSettingDCA);
      setSelectedExchange(null);
      setSelectedPair(null);
      setErrors({});
    }
  }, [openDialogBot]);

  const settingsFields = [
    {
      name: "amount",
      label: "Trade Size",
      placeholder: "Trade Size",
      rightIcon: selectedPair?.base_currency || "USDT",
      explanation: "Total capital the bot can use.",
    },
    {
      name: "max_order_count",
      label: "Maximum order",
      placeholder: "Maximum order",
      rightIcon: "orders",
      explanation: "Limit on total DCA buys.",
    },
    {
      name: "price_change_percentage",
      label: "Price change",
      placeholder: "Price change",
      rightIcon: "%",
      explanation: "Price move needed to place next buy.",
    },
    {
      name: "take_profit_percentage",
      label: "Take profit (buy back)",
      placeholder: "Take profit (buy back)",
      rightIcon: "%",
      explanation: "Profit level to secure gains.",
    },
    {
      name: "trigger_price",
      label: "Trigger price",
      placeholder: "Trigger price",
      rightIcon: selectedPair?.base_currency || "USDT",
      explanation: "Price where DCA starts.",
    },
  ];

  return (
    <>
      <DropdownExchange
        id="exchange"
        value={selectedExchange}
        onChange={handleDropdownChange(setSelectedExchange)}
        options={
          !!exchanges?.length ? exchanges.filter((exch) => exch?.id === 1) : []
        }
        loading={loadingExchanges}
        disabled={!!formDCA?.exchange_name}
        placeholder={formDCA?.exchange_name || "Select Exchange"}
      />
      <DropdownPair
        id="pair"
        value={selectedPair}
        onChange={setSelectedPair}
        options={pairs}
        loading={loadingPair}
        disabled={!!formDCA?.coin_name}
        placeholder={formDCA?.coin_name || "Select Pair"}
      />

      <div className="mt-4">
        <AvailableForTrade
          userBalance={userBalance}
          exchangeName={formDCA?.exchange_name || selectedExchange?.name}
          coinName={formDCA?.coin_name || selectedPair?.pair}
        />
      </div>

      <div className="mt-4">
        <span className="text-[#697077] text-sm block mb-2">
          Capital Allocation
        </span>
        <InputWithIcon
          type="text"
          inputMode="decimal"
          name="capital"
          value={settings.capital}
          onChange={(e) => handleSettingChange(e.target.name, e.target.value)}
          onBlur={(e) => handleBlur(e.target.name, e.target.value)}
          placeholder="Capital Allocation"
          className="mb-4"
          rightIcon={"USDT"}
        />
        <CustomSlider
          percentage={capitalPercentage}
          setPercentage={handleSliderChange}
        />
        {errors?.capital && (
          <p className="text-red-500 text-sm mt-1.5">{errors?.capital}</p>
        )}
      </div>

      <div className="border border-gray-300 mt-5 rounded-[10px] relative">
        <div className="bg-[#F2F4F8] p-3">
          <span className="text-black block">Settings</span>
          <span className="block text-[#697077]">Setting Your DCA Bot</span>
        </div>

        <div className="p-3 grid gap-4 grid-cols-2 mt-3">
          {settingsFields.map((field) => {
            const fieldError = errors[field.name];

            return (
              <div
                key={field.name}
                className={field.name === "trigger_price" ? "col-span-2" : ""}
              >
                <LabelWithTooltip
                  label={field.label}
                  explanation={field.explanation}
                />
                <InputWithIcon
                  id={field.name}
                  name={field.name}
                  value={settings[field.name]}
                  onChange={(e) =>
                    handleSettingChange(field.name, e.target.value)
                  }
                  onBlur={(e) => handleBlur(field.name, e.target.value)}
                  placeholder={field.placeholder}
                  rightIcon={field.rightIcon}
                  type="text"
                  inputMode="decimal"
                />
                {fieldError && (
                  <p className="text-red-500 text-sm mt-1.5">{fieldError}</p>
                )}
              </div>
            );
          })}
          {/* <div className="col-span-2 text-sm text-red-500 mb-2">
            Stop loss is smaller than combined price changes, hence maximum
            order will not be reached.
          </div> */}
        </div>
      </div>
    </>
  );
};

export default DCAComponent;
